/**
 * The honest empty state.
 *
 * Roughly 96% of Saudi districts have too few registered deals to say anything
 * reliable, so this is not a rare edge case — it is a state the card reaches
 * often, and it has to look considered rather than broken. It explains WHY
 * there is no number, because "not enough data" with a reason reads as rigour
 * and "not enough data" on its own reads as a failure.
 */

import { useTranslation } from 'react-i18next';
import type { MarketUnavailableReason } from '../../types/marketData';

export type MarketEmptyKind = MarketUnavailableReason | 'not-enough-deals' | 'no-segment' | 'rent-only';

export interface MarketEmptyProps {
  kind: MarketEmptyKind;
  className?: string;
}

const REASON_KEY: Record<MarketEmptyKind, string> = {
  'not-enough-deals': 'empty.notEnoughDeals',
  'district-unknown': 'empty.districtUnknown',
  'no-segment': 'empty.noSegment',
  'rent-only': 'empty.rentOnly',
  'snapshot-unavailable': 'empty.unavailable',
  'request-failed': 'empty.unavailable',
};

export default function MarketEmpty({ kind, className = '' }: MarketEmptyProps) {
  const { t } = useTranslation('marketData');

  return (
    <div
      className={`rounded-brand-lg border border-dashed border-[var(--pv-border-soft)] bg-[var(--pv-color-surface-soft)] p-5 ${className}`}
    >
      <div className="flex items-start gap-3">
        <i
          className="ri-bar-chart-box-line mt-0.5 shrink-0 text-lg text-[var(--pv-color-text-subtle)]"
          aria-hidden="true"
        />
        <div>
          <p className="text-sm font-semibold text-[var(--pv-color-text)]">
            {t('empty.title', { defaultValue: 'Not enough data' })}
          </p>
          <p className="mt-1 text-[13px] leading-relaxed text-[var(--pv-color-text-muted)]">
            {t(REASON_KEY[kind], { defaultValue: 'There is not enough registered data to show a reliable figure here.' })}
          </p>
        </div>
      </div>
    </div>
  );
}
